import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../lib/api';
import { Plus, Trash2, Send, Power, X, Copy, CheckCircle } from 'lucide-react';

const EVENT_TYPES = [
  'user.login',
  'user.created',
  'user.deleted',
  'password.changed',
  'mfa.enabled',
  'mfa.disabled',
];

const WebhookModal: React.FC<{ isOpen: boolean; onClose: () => void; onSaved: (secret?: string) => void }> = ({ isOpen, onClose, onSaved }) => {
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<string[]>(['*']);
  const create = useMutation({
    mutationFn: () => adminAPI.createWebhook({ url, events, description }),
    onSuccess: (w) => {
      onSaved(w.secret);
      setUrl('');
      setDescription('');
      setEvents(['*']);
    },
  });

  if (!isOpen) return null;
  const toggle = (e: string) =>
    setEvents((prev) => (e === '*' ? ['*'] : prev.filter((x) => x !== '*').includes(e) ? prev.filter((x) => x !== e) : [...prev.filter((x) => x !== '*'), e]));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Add Webhook</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/webhooks/authaas" className="w-full border border-gray-300 rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
            <label className="flex items-center gap-2 text-sm mb-1">
              <input type="checkbox" checked={events.includes('*')} onChange={() => toggle('*')} /> All events (*)
            </label>
            {EVENT_TYPES.map((e) => (
              <label key={e} className="flex items-center gap-2 text-sm">
                <input type="checkbox" disabled={events.includes('*')} checked={events.includes(e)} onChange={() => toggle(e)} /> {e}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md">Cancel</button>
            <button onClick={() => create.mutate()} disabled={!url || create.isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
              {create.isLoading ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WebhooksPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: webhooks = [], isLoading } = useQuery({ queryKey: ['webhooks'], queryFn: () => adminAPI.getWebhooks() });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['webhooks'] });

  const del = useMutation({ mutationFn: (id: string) => adminAPI.deleteWebhook(id), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: ({ id, active }: { id: string; active: boolean }) => adminAPI.updateWebhook(id, { active }), onSuccess: refresh });
  const test = useMutation({
    mutationFn: (id: string) => adminAPI.testWebhook(id),
    onSuccess: (r) => alert(r.delivered ? `Delivered (HTTP ${r.status})` : `Not delivered: ${r.error || r.status}`),
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Webhooks</h1>
          <p className="text-gray-600">Receive signed HTTP callbacks when events occur.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center">
          <Plus className="w-4 h-4 mr-2" /> Add Webhook
        </button>
      </div>

      {newSecret && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-800 font-medium mb-1">Webhook created. Copy its signing secret now — it won't be shown again:</p>
          <div className="flex items-center gap-2">
            <code className="text-sm bg-white px-2 py-1 rounded border break-all">{newSecret}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(newSecret); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              className="text-green-700 flex items-center text-sm"
            >
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
            <button onClick={() => setNewSecret(null)} className="ml-auto text-gray-400"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-green-700 mt-2">Verify deliveries with the <code>X-Authaas-Signature: sha256=&lt;hmac&gt;</code> header (HMAC-SHA256 of the raw body).</p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : webhooks.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No webhooks yet. Add one to start receiving events.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Events</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {webhooks.map((w: any) => (
                <tr key={w.id}>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-800 break-all">{w.url}</p>
                    {w.description && <p className="text-xs text-gray-500">{w.description}</p>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{(w.events || []).join(', ')}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${w.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {w.active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => test.mutate(w.id)} title="Send test" className="text-gray-500 hover:text-blue-600"><Send className="w-4 h-4 inline" /></button>
                    <button onClick={() => toggle.mutate({ id: w.id, active: !w.active })} title="Toggle" className="text-gray-500 hover:text-amber-600"><Power className="w-4 h-4 inline" /></button>
                    <button onClick={() => confirm('Delete this webhook?') && del.mutate(w.id)} title="Delete" className="text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <WebhookModal isOpen={showModal} onClose={() => setShowModal(false)} onSaved={(secret) => { setShowModal(false); setNewSecret(secret || null); refresh(); }} />
    </div>
  );
};

export default WebhooksPage;
